import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ FIN-02 at v1.993 (the credit-notes slice). A credit is drafted from an issued invoice,
// changed whole while a draft, and issued as an adjustment note. Credit it holds is refunded from
// it, or applied to another of the client's invoices from that invoice's page.

export type CreditNote = components['schemas']['CreditNoteView'];
export type CreditNoteSummary = components['schemas']['CreditNoteSummaryView'];
export type CreditReason = components['schemas']['CreditReasonView'];
export type CreditReasonKind = CreditReason['kind'];

/** The fixed kinds a tenant's reasons sit under, as the reasons screen names them. */
export const REASON_KINDS: ReadonlyArray<readonly [CreditReasonKind, string]> = [
  ['error', 'Error'],
  ['not_supplied', 'Not supplied'],
  ['cancelled', 'Cancelled'],
  ['revised', 'Revised'],
  ['goodwill', 'Goodwill'],
  ['other', 'Other'],
];

/** A draft's content, sent whole on each save. */
export interface CreditNoteInput {
  readonly reasonId: string | null;
  readonly note: string | null;
  readonly lines: ReadonlyArray<
    | { readonly invoiceLineId: string; readonly quantity: number }
    | { readonly invoiceLineId: string; readonly amountMinor: string }
  >;
  readonly amounts: ReadonlyArray<{
    readonly description: string;
    readonly amountMinor: string;
    readonly taxCodeId: string;
  }>;
}

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const creditNotesQuery = (filter: { clientId?: string; held?: boolean } = {}) =>
  queryOptions({
    queryKey: ['credit-notes', filter],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/credit-notes', {
        params: {
          query: {
            ...(filter.clientId ? { clientId: filter.clientId } : {}),
            ...(filter.held ? { held: 'true' } : {}),
          },
        },
      });
      if (error) throw problem(error, 'Could not load the credits.');
      return data;
    },
  });

export const creditNoteQuery = (id: string) =>
  queryOptions({
    queryKey: ['credit-notes', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/credit-notes/{id}', { params: { path: { id } } });
      if (error) throw problem(error, 'Could not load that credit.');
      return data;
    },
  });

export const creditReasonsQuery = queryOptions({
  queryKey: ['credit-note-reasons'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/credit-note-reasons');
    if (error) throw problem(error, 'Could not load the reasons.');
    return data;
  },
});

/** An empty draft against an issued invoice, to fill in on its own page. */
export async function draftCreditNote(invoiceId: string) {
  const { data, error } = await api.POST('/v1/invoices/{id}/credit-notes', {
    params: { path: { id: invoiceId } },
    body: { reasonId: null, note: null, lines: [], amounts: [] },
  });
  if (error) throw problem(error, 'Could not start a credit.');
  return data;
}

export async function saveCreditNote(id: string, input: CreditNoteInput) {
  const { data, error } = await api.PATCH('/v1/credit-notes/{id}', {
    params: { path: { id } },
    body: { ...input, lines: [...input.lines], amounts: [...input.amounts] },
  });
  if (error) throw problem(error, 'Could not save that credit.');
  return data;
}

export async function discardCreditNote(id: string) {
  const { error } = await api.DELETE('/v1/credit-notes/{id}', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not discard that credit.');
}

/** FIN-18: numbered AN- now, and final. */
export async function issueCreditNote(id: string) {
  const { data, error } = await api.POST('/v1/credit-notes/{id}/issue', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not issue that credit.');
  return data;
}

export async function refundCredit(
  id: string,
  input: {
    amountMinor: string;
    refundedOn: string;
    method: 'bank_transfer' | 'card' | 'cash' | 'other';
    reference: string | null;
  },
) {
  const { data, error } = await api.POST('/v1/credit-notes/{id}/refunds', {
    params: { path: { id } },
    body: input,
  });
  if (error) throw problem(error, 'Could not record that refund.');
  return data;
}

export async function applyCredit(id: string, invoiceId: string, amountMinor: string) {
  const { data, error } = await api.POST('/v1/credit-notes/{id}/applications', {
    params: { path: { id } },
    body: { invoiceId, amountMinor },
  });
  if (error) throw problem(error, 'Could not apply that credit.');
  return data;
}

export async function addCreditReason(label: string, kind: CreditReasonKind) {
  const { data, error } = await api.POST('/v1/credit-note-reasons', { body: { label, kind } });
  if (error) throw problem(error, 'Could not add that reason.');
  return data;
}

export async function updateCreditReason(id: string, change: { label?: string; retired?: true }) {
  const { data, error } = await api.PATCH('/v1/credit-note-reasons/{id}', {
    params: { path: { id } },
    body: change,
  });
  if (error) throw problem(error, 'Could not change that reason.');
  return data;
}

/** "Draft", "Issued", or "Credit held" while it holds credit to refund or apply. */
export function creditState(c: CreditNoteSummary): string {
  if (c.status === 'draft') return 'Draft';
  return BigInt(c.held.amountMinor) > 0n ? 'Credit held' : 'Issued';
}
